/**
 * VOY-73: RateHawk ETG Status and Error Code Mappings
 * 
 * This file provides centralized mappings for all RateHawk booking statuses
 * and error codes to user-friendly messages and UI states.
 */

export type BookingStatus = 
  | 'new'
  | 'pending'
  | 'confirmed'
  | 'cancelled'
  | 'failed'
  | 'modified'
  | 'on_request'
  | 'processing'
  | 'timeout'
  | 'unknown';

export type ErrorCode =
  | 'unavailable'
  | 'price_changed'
  | 'invalid_request'
  | 'payment_failed'
  | 'timeout'
  | 'network_error'
  | 'rate_limit'
  | 'invalid_credentials'
  | 'booking_not_found'
  | 'cancellation_failed'
  | 'refund_failed'
  | 'unknown_error';

/**
 * Booking Status Information
 */
export interface StatusInfo {
  label: string;
  message: string;
  description: string;
  icon: 'success' | 'warning' | 'error' | 'info' | 'loading';
  color: 'green' | 'yellow' | 'red' | 'blue' | 'gray';
  canRetry: boolean;
  nextSteps: string[];
  supportContact?: boolean;
}

/**
 * Error Information
 */
export interface ErrorInfo {
  title: string;
  message: string;
  description: string;
  canRetry: boolean;
  retryAction?: string;
  nextSteps: string[];
  supportContact: boolean;
}

/**
 * Booking Status Mappings
 */
export const BOOKING_STATUS_MAP: Record<BookingStatus, StatusInfo> = {
  new: {
    label: 'Booking Initiated',
    message: 'Your booking request has been received',
    description: 'We\'re processing your booking request. This usually takes just a few moments.',
    icon: 'info',
    color: 'blue',
    canRetry: false,
    nextSteps: [
      'Please wait while we confirm your booking',
      'You\'ll receive a confirmation email shortly'
    ]
  },
  pending: {
    label: 'Awaiting Confirmation',
    message: 'Your booking is being confirmed',
    description: 'We\'re waiting for confirmation from the hotel. This typically takes 1-2 minutes.',
    icon: 'loading',
    color: 'yellow',
    canRetry: false,
    nextSteps: [
      'Please wait while we confirm availability',
      'You\'ll be notified as soon as your booking is confirmed',
      'Check your email for updates'
    ]
  },
  confirmed: {
    label: 'Booking Confirmed',
    message: 'Your booking has been confirmed!',
    description: 'Your hotel reservation is confirmed. A confirmation email has been sent to you.',
    icon: 'success',
    color: 'green',
    canRetry: false,
    nextSteps: [
      'Check your email for booking confirmation',
      'Save your confirmation number',
      'Contact the hotel directly if you need to make changes'
    ]
  },
  cancelled: {
    label: 'Booking Cancelled',
    message: 'Your booking has been cancelled',
    description: 'Your booking has been successfully cancelled. Any refunds will be processed according to the cancellation policy.',
    icon: 'info',
    color: 'gray',
    canRetry: false,
    nextSteps: [
      'Check your email for cancellation confirmation',
      'Refunds typically process within 5-10 business days',
      'Contact support if you have questions about your refund'
    ],
    supportContact: true
  },
  failed: {
    label: 'Booking Failed',
    message: 'We couldn\'t complete your booking',
    description: 'Unfortunately, we were unable to complete your booking. This may be due to availability changes or a temporary issue.',
    icon: 'error',
    color: 'red',
    canRetry: true,
    nextSteps: [
      'Try booking again with the same or different dates',
      'Check if the room is still available',
      'Contact support if the problem persists'
    ],
    supportContact: true
  },
  modified: {
    label: 'Booking Modified',
    message: 'Your booking has been updated',
    description: 'Changes have been made to your booking. Please review the updated details.',
    icon: 'info',
    color: 'blue',
    canRetry: false,
    nextSteps: [
      'Check your email for updated confirmation',
      'Review the new booking details',
      'Contact support if you have questions'
    ],
    supportContact: true
  },
  on_request: {
    label: 'Booking on Request',
    message: 'Your booking is being processed',
    description: 'This booking requires manual confirmation from the hotel. We\'ll notify you once it\'s confirmed.',
    icon: 'loading',
    color: 'yellow',
    canRetry: false,
    nextSteps: [
      'Please wait for hotel confirmation (usually within 24 hours)',
      'You\'ll receive an email once confirmed',
      'Contact support if you need to cancel before confirmation'
    ],
    supportContact: true
  },
  processing: {
    label: 'Processing',
    message: 'Your booking is being processed',
    description: 'Payment received! We\'re confirming your booking with the hotel. This usually takes just a few moments.',
    icon: 'loading',
    color: 'blue',
    canRetry: false,
    nextSteps: [
      'Please wait while we confirm your booking',
      'You\'ll receive a confirmation email shortly',
      'This typically takes 10-30 seconds'
    ]
  },
  timeout: {
    label: 'Request Timeout',
    message: 'The booking request took too long',
    description: 'The booking request timed out. Your payment may have been processed, but we need to verify the booking status.',
    icon: 'warning',
    color: 'yellow',
    canRetry: true,
    nextSteps: [
      'Check your email for booking confirmation',
      'Contact support to verify your booking status',
      'Do not attempt to book again until status is confirmed'
    ],
    supportContact: true
  },
  unknown: {
    label: 'Unknown Status',
    message: 'Unable to determine booking status',
    description: 'We couldn\'t determine the current status of your booking. Please contact support for assistance.',
    icon: 'warning',
    color: 'yellow',
    canRetry: false,
    nextSteps: [
      'Contact support with your order number',
      'Check your email for any updates',
      'Do not attempt to book again until status is confirmed'
    ],
    supportContact: true
  }
};

/**
 * Error Code Mappings
 */
export const ERROR_CODE_MAP: Record<ErrorCode, ErrorInfo> = {
  unavailable: {
    title: 'Room No Longer Available',
    message: 'Sorry, this room is no longer available',
    description: 'The room you selected is no longer available for the selected dates. This can happen if someone else booked it or the hotel updated availability.',
    canRetry: true,
    retryAction: 'Search for alternative rooms',
    nextSteps: [
      'Try selecting different dates',
      'Search for other rooms at the same hotel',
      'Look for similar hotels in the area'
    ],
    supportContact: false
  },
  price_changed: {
    title: 'Price Has Changed',
    message: 'The price has changed. Please review before continuing',
    description: 'The room price has been updated since you started your booking. Please review the new price before confirming.',
    canRetry: true,
    retryAction: 'Review and confirm new price',
    nextSteps: [
      'Review the updated price',
      'Confirm if you want to proceed',
      'Cancel if the new price doesn\'t work for you'
    ],
    supportContact: false
  },
  invalid_request: {
    title: 'Invalid Booking Details',
    message: 'There was an error with your booking details',
    description: 'Some of the information provided is invalid or incomplete. Please check your booking details and try again.',
    canRetry: true,
    retryAction: 'Review and correct booking details',
    nextSteps: [
      'Check all guest information is correct',
      'Verify dates and room selection',
      'Ensure all required fields are filled'
    ],
    supportContact: false
  },
  payment_failed: {
    title: 'Payment Failed',
    message: 'Payment failed. Please check your card details',
    description: 'We couldn\'t process your payment. This could be due to insufficient funds, incorrect card details, or your bank declining the transaction.',
    canRetry: true,
    retryAction: 'Try payment again',
    nextSteps: [
      'Check your card details are correct',
      'Verify you have sufficient funds',
      'Contact your bank if the problem persists',
      'Try a different payment method'
    ],
    supportContact: true
  },
  timeout: {
    title: 'Request Timed Out',
    message: 'The booking request timed out. Please try again',
    description: 'The booking request took too long to process. Your payment may have been processed, so please check before trying again.',
    canRetry: true,
    retryAction: 'Check status or try again',
    nextSteps: [
      'Check your email for booking confirmation',
      'Contact support to verify booking status',
      'Wait a few minutes before trying again'
    ],
    supportContact: true
  },
  network_error: {
    title: 'Connection Error',
    message: 'We couldn\'t connect to our servers. Please check your internet connection',
    description: 'There was a problem connecting to our servers. This is usually a temporary network issue.',
    canRetry: true,
    retryAction: 'Try again',
    nextSteps: [
      'Check your internet connection',
      'Wait a moment and try again',
      'Contact support if the problem persists'
    ],
    supportContact: false
  },
  rate_limit: {
    title: 'Too Many Requests',
    message: 'You\'ve made too many requests. Please wait a moment',
    description: 'For security reasons, we limit the number of requests. Please wait a few moments before trying again.',
    canRetry: true,
    retryAction: 'Wait and try again',
    nextSteps: [
      'Wait 1-2 minutes before trying again',
      'If the problem persists, contact support'
    ],
    supportContact: false
  },
  invalid_credentials: {
    title: 'Authentication Error',
    message: 'There was an authentication error. Please try logging in again',
    description: 'Your session may have expired. Please log in again to continue.',
    canRetry: true,
    retryAction: 'Log in again',
    nextSteps: [
      'Log out and log back in',
      'Clear your browser cache if the problem persists',
      'Contact support if you continue to have issues'
    ],
    supportContact: true
  },
  booking_not_found: {
    title: 'Booking Not Found',
    message: 'We couldn\'t find your booking',
    description: 'The booking you\'re looking for doesn\'t exist or may have been cancelled.',
    canRetry: false,
    nextSteps: [
      'Check your order number is correct',
      'Look for confirmation emails',
      'Contact support with your order details'
    ],
    supportContact: true
  },
  cancellation_failed: {
    title: 'Cancellation Failed',
    message: 'We couldn\'t cancel your booking',
    description: 'There was an error cancelling your booking. This may be due to the cancellation deadline passing or a temporary issue.',
    canRetry: true,
    retryAction: 'Try cancelling again',
    nextSteps: [
      'Check the cancellation policy',
      'Try cancelling again',
      'Contact support if you need immediate assistance'
    ],
    supportContact: true
  },
  refund_failed: {
    title: 'Refund Processing Error',
    message: 'There was an error processing your refund',
    description: 'Your booking was cancelled, but there was an issue processing the refund. Don\'t worry, we\'re working on it.',
    canRetry: false,
    nextSteps: [
      'Refunds typically process within 5-10 business days',
      'Contact support with your order number',
      'Check your payment method for the refund'
    ],
    supportContact: true
  },
  unknown_error: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred',
    description: 'We encountered an unexpected error. Our team has been notified and is working on a fix.',
    canRetry: true,
    retryAction: 'Try again',
    nextSteps: [
      'Try again in a few moments',
      'Contact support if the problem persists',
      'Include any error details when contacting support'
    ],
    supportContact: true
  }
};

/**
 * VOY-236: Map internal order status values to BookingStatus
 * 
 * Our orders table uses internal status values (created, paid, confirming, etc.)
 * that need to be mapped to user-facing BookingStatus values.
 */
export function mapOrderStatusToBookingStatus(orderStatus: string | null | undefined): BookingStatus {
  if (!orderStatus) {
    return 'unknown';
  }
  
  const normalized = orderStatus.toLowerCase().trim();
  
  // Map internal order statuses to booking statuses
  switch (normalized) {
    case 'created':
      return 'new'; // Order created but payment not yet processed
    case 'paid':
      return 'processing'; // Payment successful, booking confirmation in progress
    case 'holding':
    case 'holding_partial':
      return 'pending'; // Holds/prebooks created, awaiting confirmation
    case 'confirming':
      return 'processing'; // Actively confirming with supplier
    case 'confirmed':
      return 'confirmed'; // Booking confirmed by supplier
    case 'failed':
      return 'failed'; // Booking failed
    case 'cancelled':
      return 'cancelled'; // Booking cancelled
    default:
      // If it's already a valid BookingStatus, return it
      if (normalized in BOOKING_STATUS_MAP) {
        return normalized as BookingStatus;
      }
      return 'unknown';
  }
}

/**
 * Helper function to get status info
 * 
 * VOY-236: Now maps internal order statuses to booking statuses first
 */
export function getStatusInfo(status: string | null | undefined): StatusInfo {
  if (!status) {
    return BOOKING_STATUS_MAP.unknown;
  }
  
  // First, try to map internal order status to booking status
  const bookingStatus = mapOrderStatusToBookingStatus(status);
  
  // Return the mapped status info
  return BOOKING_STATUS_MAP[bookingStatus] || BOOKING_STATUS_MAP.unknown;
}

/**
 * Helper function to get error info
 */
export function getErrorInfo(errorCode: string | null | undefined): ErrorInfo {
  if (!errorCode) {
    return ERROR_CODE_MAP.unknown_error;
  }
  
  const normalizedCode = errorCode.toLowerCase().trim().replace(/[^a-z_]/g, '_') as ErrorCode;
  return ERROR_CODE_MAP[normalizedCode] || ERROR_CODE_MAP.unknown_error;
}

/**
 * Helper function to extract error code from error message or object
 */
export function extractErrorCode(error: unknown): ErrorCode {
  if (!error) return 'unknown_error';
  
  let errorString = '';
  if (typeof error === 'string') {
    errorString = error;
  } else if (error && typeof error === 'object') {
    if ('code' in error && typeof error.code === 'string') {
      errorString = error.code;
    } else if ('error' in error && typeof error.error === 'string') {
      errorString = error.error;
    } else if ('message' in error && typeof error.message === 'string') {
      errorString = error.message;
    }
  }
  
  const lowerError = errorString.toLowerCase();
  
  // Map common error patterns to error codes
  if (lowerError.includes('unavailable') || lowerError.includes('not available')) {
    return 'unavailable';
  }
  if (lowerError.includes('price') && (lowerError.includes('change') || lowerError.includes('update'))) {
    return 'price_changed';
  }
  if (lowerError.includes('invalid') || lowerError.includes('missing') || lowerError.includes('required')) {
    return 'invalid_request';
  }
  if (lowerError.includes('payment') || lowerError.includes('card') || lowerError.includes('charge')) {
    return 'payment_failed';
  }
  if (lowerError.includes('timeout') || lowerError.includes('timed out')) {
    return 'timeout';
  }
  if (lowerError.includes('network') || lowerError.includes('connection') || lowerError.includes('fetch')) {
    return 'network_error';
  }
  if (lowerError.includes('rate limit') || lowerError.includes('too many')) {
    return 'rate_limit';
  }
  if (lowerError.includes('auth') || lowerError.includes('login') || lowerError.includes('session')) {
    return 'invalid_credentials';
  }
  if (lowerError.includes('not found') || lowerError.includes('doesn\'t exist')) {
    return 'booking_not_found';
  }
  if (lowerError.includes('cancel')) {
    return 'cancellation_failed';
  }
  if (lowerError.includes('refund')) {
    return 'refund_failed';
  }
  
  return 'unknown_error';
}

